import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getProfile, hasValidProfile } from "@/utils/helper";

/**
 * Wraps complaint/intake routes (demographics, complaint-history, etc.).
 *
 * Requires BOTH a valid profile AND a live session (config token + completeAuth in
 * sessionStorage). The profile persists in localStorage across days/tab-close, but the
 * session token lives in sessionStorage and is cleared on tab close — so checking the
 * profile alone would let a returning patient into the wizard with a dead session, only
 * to be kicked to login by the first failed save. Here we send them to login up front,
 * with a message, before they fill anything in.
 */
export function SSORoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const profile = getProfile();
  const hasProfile = !!profile && hasValidProfile(profile);
  const hasLiveSession =
    !!sessionStorage.getItem("config") && !!sessionStorage.getItem("completeAuth");
  const sessionExpired = hasProfile && !hasLiveSession;

  useEffect(() => {
    if (sessionExpired) {
      toast.error("Your session has expired. Please sign in again to continue.");
    }
  }, [sessionExpired]);

  if (!hasProfile) {
    return <Navigate to={location.state?.from?.pathname ?? "/"} replace />;
  }

  if (sessionExpired) {
    return <Navigate to="/account/login" replace />;
  }

  return <>{children}</>;
}
